import { formatDate, type useTranslation } from '@hbs-us/ui';

/**
 * The weekday name for an ISO date, read in UTC.
 *
 * ⚠ READ THE DAY IN UTC, ALWAYS. `new Date('2026-08-31')` is midnight UTC, and every locale
 * formatter then prints it in the reader's own zone — so west of UTC a Monday prints as Sunday and
 * every hour on the screen sits under the wrong day. The api hands out plain `YYYY-MM-DD` week
 * dates with no time in them, so there is no instant to convert and nothing is gained by trying.
 *
 * Shared by the Timesheets week grid and the Home cards so they cannot disagree about what day a
 * number belongs to.
 */
export function weekdayLabel(t: ReturnType<typeof useTranslation>['t'], isoDate: string): string {
  const dow = new Date(`${isoDate}T00:00:00Z`).getUTCDay();
  const key = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'][dow];
  return t(`timesheets:days.${key}`);
}

/**
 * A short, readable label for an ISO calendar date, read in UTC.
 *
 * Same trap as `weekdayLabel`, same fix: a bare `YYYY-MM-DD` is a calendar date with no instant in
 * it, so the formatter is pinned to UTC rather than allowed to shift the date into the reader's own
 * zone. Without the pin, a request filed on the 1st reads as the 31st west of UTC.
 */
export function calendarDateLabel(locale: string, isoDate: string): string {
  try {
    return formatDate(`${isoDate}T00:00:00Z`, locale, {
      month: 'short',
      day: 'numeric',
      timeZone: 'UTC',
    });
  } catch {
    // A malformed date from the api is still worth showing verbatim rather than swallowing.
    return isoDate;
  }
}
